import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge as UIBadge } from '@/components/ui/badge';
import { BadgeIcon } from '@/components/badges/BadgeIcon';

interface PlayerBadge {
  code: string;
  name: string;
  description: string;
  icon: string;
  metal: 'gold' | 'silver' | 'bronze';
  min_value: number;
  attributes: string[];
}

interface PlayerBadgesProps {
  playerId: string;
  max?: number;
}

const METAL_STYLES: Record<PlayerBadge['metal'], { ring: string; text: string; glow: string; label: string }> = {
  gold: {
    ring: 'border-metal-gold/70 bg-[radial-gradient(circle_at_30%_25%,hsl(var(--metal-gold)/0.28),hsl(var(--metal-gold)/0.06)_70%)]',
    text: 'text-metal-gold-fg',
    glow: 'shadow-[0_0_12px_-2px_hsl(var(--metal-gold)/0.55)] hover:shadow-[0_0_18px_-2px_hsl(var(--metal-gold)/0.75)]',
    label: 'Gold',
  },
  silver: {
    ring: 'border-metal-silver/60 bg-[radial-gradient(circle_at_30%_25%,hsl(var(--metal-silver)/0.3),hsl(var(--metal-silver)/0.06)_70%)]',
    text: 'text-metal-silver-fg',
    glow: 'shadow-[0_0_12px_-2px_hsl(var(--metal-silver)/0.5)] hover:shadow-[0_0_18px_-2px_hsl(var(--metal-silver)/0.7)]',
    label: 'Silver',
  },
  bronze: {
    ring: 'border-metal-bronze/60 bg-[radial-gradient(circle_at_30%_25%,hsl(var(--metal-bronze)/0.28),hsl(var(--metal-bronze)/0.06)_70%)]',
    text: 'text-metal-bronze-fg',
    glow: 'shadow-[0_0_10px_-3px_hsl(var(--metal-bronze)/0.5)] hover:shadow-[0_0_16px_-2px_hsl(var(--metal-bronze)/0.7)]',
    label: 'Bronze',
  },
};

const formatAttribute = (attr: string) =>
  attr.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function PlayerBadges({ playerId, max }: PlayerBadgesProps) {
  const [badges, setBadges] = useState<PlayerBadge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_player_badges', { p_player_id: playerId });
        if (error) throw error;
        if (active) setBadges((data as PlayerBadge[]) || []);
      } catch (e) {
        console.error('Error loading player badges:', e);
        if (active) setBadges([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [playerId]);

  if (loading || badges.length === 0) return null;

  const visible = typeof max === 'number' ? badges.slice(0, max) : badges;
  const overflow = badges.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
        {visible.map((badge) => {
          const style = METAL_STYLES[badge.metal];
          return (
            <Popover key={badge.code}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={`${badge.name} badge`}
                  className={`flex h-11 w-11 items-center justify-center rounded-full border-2 bg-card ${style.ring} ${style.glow} cursor-pointer transition-all motion-safe:hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background`}
                >
                  <BadgeIcon code={badge.code} icon={badge.icon} className={`h-5 w-5 ${style.text}`} />
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="center" className="w-72 space-y-3 p-4">
                <div className="flex items-start gap-3">
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 bg-card ${style.ring}`} aria-hidden="true">
                    <BadgeIcon code={badge.code} icon={badge.icon} className={`h-4 w-4 ${style.text}`} />
                  </div>
                  <div className="min-w-0">
                  <p className="font-display font-semibold text-sm">
                    {badge.name} <span className={`font-semibold ${style.text}`}>· {style.label}</span>
                  </p>
                  <p className="text-xs leading-relaxed text-muted-foreground mt-0.5">{badge.description}</p>
                  </div>
                </div>
                {badge.attributes?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Built from
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {badge.attributes.map((a) => (
                        <UIBadge key={a} variant="secondary" className="text-[10px]">
                          {formatAttribute(a)}
                        </UIBadge>
                      ))}
                    </div>
                  </div>
                )}
                <Link
                  to="/badges"
                  className="block text-xs font-medium text-primary hover:underline pt-2.5 border-t border-border/60"
                >
                  See all badges →
                </Link>
              </PopoverContent>
            </Popover>
          );
        })}

        {overflow > 0 && (
          <Link
            to={`/player/${playerId}`}
            aria-label={`${overflow} more badges`}
            className="flex h-11 w-11 items-center justify-center rounded-full border-2 bg-muted border-border transition-transform motion-safe:hover:-translate-y-0.5"
          >
            <span className="text-xs font-semibold text-muted-foreground">+{overflow}</span>
          </Link>
        )}
    </div>
  );
}
